import { useCallback, useContext, useMemo, useState } from 'react';
import { OrganisationContext, UserContext } from '@database/contexts';
import { userService } from '@database/services';
import { ErrorType, User, UserShopRoleEnum } from '@utilities/types';

export const useUser = () => {

    const { 
        user, setUser,
        temporaryToken, setTemporaryToken,
        collaborators, setCollaborators
    } = useContext(UserContext);
    const { 
        organisation, setOrganisation,
        setIsUserAdmin,
    } = useContext(OrganisationContext);

    const [emailVerifyisSuccess, setEmailVerifyisSuccess] = useState<boolean>(false);
    const [createuserPasswordSuccess, setCreateUserPasswordSuccess] = useState<boolean>(false);
    const [signinError, setSigninError] = useState<ErrorType | null>(null);
    const [createAccountError, setCreateAccountError] = useState<ErrorType | null>(null);
    const [verifyEmailError, setVerifyEmailError] = useState<ErrorType | null>(null);
    const [createPasswordError, setCreatePasswordError] = useState<ErrorType | null>(null);
    const [updateUserError, setUpdateUserError] = useState<ErrorType | null>(null);
    const [updateUserPasswordError, setUpdateUserPasswordError] = useState<ErrorType | null>(null);
    const [getCollaboratorsError, setGetCollaboratorsError] = useState<ErrorType | null>(null);
    const [forgotPasswordError, setForgotPasswordError] = useState<ErrorType | null>(null);

    const isUserInfosIsComplete = useMemo(() => !!(
        user && user.firstname && user.lastname && user.email
        && user.phone && user.birthDate
    ), [user]);

    const onSigninUser = useCallback(async(email: string, password: string)=> {
        const req = await userService.login({ email, password });
        if(req.code === 200){
            setUser(req.data.user);
            setOrganisation(req.data.organisation);
            setIsUserAdmin(req.data.user.role !== UserShopRoleEnum.seller);
            setSigninError(null);
        } else {
            setSigninError({
                message: (req.code === 400 || req.code === 404)
                        ? "Email ou mot de passe incorrect"
                        : "Une erreur est survenue, veuillez réessayer",
            });
        }
    }, [setIsUserAdmin, setOrganisation, setUser]);
    
    const onCreateAccount = useCallback(async(email: string, role?: UserShopRoleEnum)=> {
        const req = await userService.create({
            email, 
            role: organisation ? role as UserShopRoleEnum : UserShopRoleEnum.admin,
            organisationID: organisation ? organisation._id : undefined,
        });

        if(req.code === 201){
            setTemporaryToken(req.data.token);
            setCreateAccountError(null);
        } else {
            setCreateAccountError({
                message: (req.code === 400)
                        ? "Email déjà utilisé ou invalide"
                        : "Une erreur est survenue, veuillez réessayer",
            });
        }
    }, [
        organisation, 
        setCreateAccountError, setTemporaryToken,
    ]);

    const onResendMailOtp = useCallback(async(token: string)=> {
        const req = await userService.resendOtp(token);

        if(req.code === 201){
            setCreateAccountError(null);
        } else {
            setCreateAccountError({
                message: (req.code === 400)
                        ? "Email déjà utilisé ou invalide"
                        : "Une erreur est survenue, veuillez réessayer",
            });
        }
    }, [setCreateAccountError]);

    const onVerifyEmail = useCallback(async(email: string, otpCode: string)=> {
        const req = await userService.verifyEmail({ email, otpCode });
        if(req.code === 200){
            setTemporaryToken(req.data.token);
            setVerifyEmailError(null);
            setEmailVerifyisSuccess(true);
        } else {
            setVerifyEmailError({
                message: (req.code === 400 || req.code === 404)
                        ? "Mail de vérification invalide ou déjà utilisé"
                        : "Une erreur est survenue, veuillez réessayer",
            });
            setEmailVerifyisSuccess(false);
        }
    }, [setTemporaryToken, setVerifyEmailError]);

    const onCreateUserPassword = useCallback(async(token: string, password: string)=> {
        const req = await userService.createUserPassword({ token, password });

        if(req.code === 200){
            setCreatePasswordError(null);
            setCreateUserPasswordSuccess(true);
        } else {
            setCreatePasswordError({
                message: (req.code === 400 || req.code === 404)
                        ? "Email ou mot de passe incorrect"
                        : "Une erreur est survenue, veuillez réessayer",
            });
        }
    }, []);

    const onUpdatedUserInfos = useCallback(async(userData: Partial<User>)=> {
        const req = await userService.updateUser(userData);

        if(req.code === 200){
            setUser(req.data.user);
            setUpdateUserError(null);
        } else {
            setUpdateUserError({
                message: (req.code === 400 || req.code === 404)
                        ? "Impossible de mettre à jour les informations"
                        : "Une erreur est survenue, veuillez réessayer",
            });
        }
    }, [setUser]);

    const onUpdatedUserPassword = useCallback(async(oldPassword: string, newPassword: string)=> {
        const req = await userService.updateUserPassword({
            token: user?.token!, oldPassword, newPassword,
        });

        if(req.code === 200){
            setUser(req.data.user);
            setUpdateUserPasswordError(null);
        } else {
            setUpdateUserPasswordError({
                message: (req.code === 400 || req.code === 404)
                        ? "Mise à jour du mot de passe échouée"
                        : "Une erreur est survenue, veuillez réessayer",
            });
        }
    }, [setUser, user]);

    const onGetOrganisationCollaborators = useCallback(async()=> {
        if(!organisation || user?.role === UserShopRoleEnum.seller) return;
        
        const req = await userService.getAllCollaborators(organisation._id);

        if(req.code === 200){
            setCollaborators(req.data);
        } else {
            setGetCollaboratorsError({
                message: (req.code === 400 || req.code === 404)
                        ? "Impossible de récupérer les collaborateurs"
                        : "Une erreur est survenue, veuillez réessayer",
            });
        }
    }, [ organisation, user, setCollaborators ]);

    const onForgotPassword = useCallback(async(email: string)=> {
        const req = await userService.forgotPassword(email);

        if(req.code === 200){
            setForgotPasswordError(null);
            return req.data; // Retourne les données pour récupérer le token si nécessaire
        } else {
            setForgotPasswordError({
                message: (req.code === 400 || req.code === 404)
                        ? "Email non trouvé ou invalide"
                        : "Une erreur est survenue, veuillez réessayer",
            });
        }
    }, []);

    return {
        isUserInfosIsComplete, updateUserPasswordError,
        user, signinError, verifyEmailError,
        createAccountError, temporaryToken,
        emailVerifyisSuccess, createuserPasswordSuccess,
        createPasswordError, updateUserError,
        collaborators, getCollaboratorsError,
        forgotPasswordError,
        setCreatePasswordError,
        onSigninUser, onCreateAccount,
        onResendMailOtp, setTemporaryToken,
        onVerifyEmail, onCreateUserPassword,
        setUser, setCollaborators,
        onUpdatedUserInfos, onUpdatedUserPassword,
        onGetOrganisationCollaborators, onForgotPassword,
    };
};
